import React, { useCallback, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Lock, Mail, Sparkles } from 'lucide-react';

import { useAuth } from '../context/AuthContext';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from '../components/ui';

const ResetPassword = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { resetPassword, updatePassword, passwordRecovery, logout } = useAuth();

  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const hashParams = useMemo(() => new URLSearchParams(location.hash.replace(/^#/, '')), [location.hash]);
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const isRecovery =
    passwordRecovery ||
    hashParams.get('type') === 'recovery' ||
    searchParams.get('type') === 'recovery';

  const handleRequestReset = useCallback(
    async (event) => {
      event.preventDefault();
      setLoading(true);
      setError('');
      setMessage('');

      try {
        await resetPassword(email);
        setMessage('If that email exists, we sent password reset instructions.');
      } catch (submissionError) {
        console.error('[auth] Failed to request password reset', submissionError);
        setError(submissionError.message ?? 'Unable to send reset email. Try again in a moment.');
      } finally {
        setLoading(false);
      }
    },
    [email, resetPassword],
  );

  const handleUpdatePassword = useCallback(
    async (event) => {
      event.preventDefault();
      setError('');
      setMessage('');

      if (!newPassword) {
        setError('Enter a new password.');
        return;
      }
      if (newPassword !== confirmPassword) {
        setError('Passwords do not match.');
        return;
      }

      setLoading(true);
      try {
        await updatePassword(newPassword);
        setMessage('Password updated! Redirecting to sign in…');
        setTimeout(() => {
          navigate('/login', { replace: true, state: { passwordReset: true } });
        }, 1200);
      } catch (submissionError) {
        console.error('[auth] Failed to update password', submissionError);
        setError(submissionError.message ?? 'Unable to update password. Try a different one.');
      } finally {
        setLoading(false);
      }
    },
    [confirmPassword, navigate, newPassword, updatePassword],
  );

  const handleBackToSignIn = useCallback(async () => {
    try {
      await logout();
    } catch (signOutError) {
      console.error('[auth] Failed to sign out during password recovery', signOutError);
    }
    navigate('/login', { replace: true });
  }, [logout, navigate]);

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-gradient-soft px-4 py-16">
      <div
        className="absolute inset-0 -z-10 blur-3xl"
        style={{
          background:
            'radial-gradient(circle at top, rgba(251, 191, 36, 0.35), transparent 45%), radial-gradient(circle at bottom, rgba(244, 114, 182, 0.25), transparent 55%)',
        }}
      />
      <div className="w-full max-w-lg">
        <div className="mb-8 text-center">
          <Link
            to="/"
            className="mx-auto flex w-fit items-center gap-3 rounded-full bg-white/60 px-4 py-2 text-sm font-semibold text-muted-foreground shadow-sm transition hover:text-foreground"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-gradient-brand text-white shadow-brand">
              <Sparkles className="h-4 w-4" />
            </span>
            Back to DoodleMagic
          </Link>
        </div>

        <Card className="border border-border/60 bg-white/90 shadow-card">
          <CardHeader>
            <CardTitle>{isRecovery ? 'Create a new password' : 'Forgot your password?'}</CardTitle>
            <CardDescription>
              {isRecovery
                ? 'Enter a new password to finish resetting your account.'
                : 'Tell us your email and we’ll send steps to reset your password.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isRecovery ? (
              <form className="space-y-5" onSubmit={handleUpdatePassword}>
                <div className="space-y-2">
                  <Label htmlFor="reset-new-password">New password</Label>
                  <div className="relative">
                    <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="reset-new-password"
                      type="password"
                      autoComplete="new-password"
                      required
                      value={newPassword}
                      onChange={(event) => setNewPassword(event.target.value)}
                      className="pl-10"
                      placeholder="Enter a new password"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reset-confirm-password">Confirm password</Label>
                  <div className="relative">
                    <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="reset-confirm-password"
                      type="password"
                      autoComplete="new-password"
                      required
                      value={confirmPassword}
                      onChange={(event) => setConfirmPassword(event.target.value)}
                      className="pl-10"
                      placeholder="Re-enter your new password"
                    />
                  </div>
                </div>
                {error && <p className="text-sm text-destructive">{error}</p>}
                {message && <p className="text-sm text-emerald-600">{message}</p>}
                <Button type="submit" className="h-11 w-full rounded-xl text-sm font-semibold" disabled={loading}>
                  {loading ? 'Updating…' : 'Update password'}
                </Button>
              </form>
            ) : (
              <form className="space-y-5" onSubmit={handleRequestReset}>
                <div className="space-y-2">
                  <Label htmlFor="reset-email">Email</Label>
                  <div className="relative">
                    <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="reset-email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      className="pl-10"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>
                {error && <p className="text-sm text-destructive">{error}</p>}
                {message && <p className="text-sm text-emerald-600">{message}</p>}
                <Button type="submit" className="h-11 w-full rounded-xl text-sm font-semibold" disabled={loading}>
                  {loading ? 'Sending…' : 'Send reset instructions'}
                </Button>
              </form>
            )}
            <div className="mt-6 flex justify-between text-sm text-muted-foreground">
              <button
                type="button"
                onClick={handleBackToSignIn}
                className="inline-flex items-center gap-2 font-semibold text-primary transition hover:text-primary/80"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to sign in
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;


